import { useState, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';
import { useParams, useNavigate } from 'react-router-dom';

import { 
  Accordion, 
  AccordionSummary, 
  AccordionDetails, 
  Box, 
  Button, 
  Typography,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  IconButton,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
} from '@mui/material';

// Icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DescriptionIcon from '@mui/icons-material/Description';
import PeopleIcon from '@mui/icons-material/People';
import HistoryIcon from '@mui/icons-material/History';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback';

// Custom Components
import {
  Spinner,
  BrowserTitle,
  AppointmentDetailsTable,
  AppointmentAppointmentsTable,
  AppointmentProposalsTable,
  AppointmentMediaTable,
  PropertyArchiveTable,
  PropertyLegacyProposalsTable,
  PropertyClientsTable,
} from '../components';

// Context
import { AuthContext } from '../context/AuthProvider';

// Utils
import messages from '../static/messages';

// Services
import appointmentsServices from '../services/appointmentsServices';

// Hooks
import useAccordion from '../hooks/useAccordion';
import PropertyWarrantyTickets from '../components/PropertyWarrantyTickets';
import ScheduledAppointmentModal from '../components/ScheduledAppointment/ScheduledAppointmentModal';
import BuildIcon from '@mui/icons-material/Build';

// External variables
const blocks = ['details', 'appointments', 'archive', 'proposals', 'legacyProposals', 'clients', 'warrantyTickets', 'media'];

// Mobile Section Card Component
const MobileSection = ({ title, icon: Icon, children, defaultExpanded = false, count = null }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  
  return (
    <Card sx={{ mb: 2, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
      <Box 
        onClick={() => setExpanded(!expanded)}
        sx={{ 
          px: 2, 
          py: 1.5, 
          backgroundColor: '#f5f5f5', 
          borderBottom: expanded ? '1px solid #eee' : 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {Icon && <Icon sx={{ mr: 1.5, color: '#1976d2', fontSize: 22 }} />}
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          {count !== null && count > 0 && (
            <Box sx={{ 
              ml: 1.5, 
              px: 1, 
              py: 0.25, 
              backgroundColor: '#1976d2', 
              borderRadius: 10,
              minWidth: 24,
              textAlign: 'center',
            }}>
              <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600 }}>
                {count}
              </Typography>
            </Box>
          )}
        </Box>
        <ExpandMoreIcon sx={{ 
          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s',
          color: '#666',
        }} />
      </Box>
      {expanded && (
        <Box sx={{ p: 0 }}>
          {children}
        </Box>
      )}
    </Card>
  );
};

const Appointment = () => {
  const [appointment, setAppointment] = useState();
  const [propertyAppointments, setPropertyAppointments] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [warrantyTicketsCount, setWarrantyTicketsCount] = useState(0);
  const [schedModalOpen, setSchedModalOpen] = useState(false);

  const { id } = useParams();
  const navigate = useNavigate();
  const { open, accordionToggle } = useAccordion(blocks);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Get user role from context
  const { userData } = useContext(AuthContext);
  const isTechnician = userData?.role === 'technician';
  const isCancelled = appointment?.status === 'Cancelled';

  // Async functions
  const getAppointmentData = async () => {
    try {
      const response = await appointmentsServices.getAppointmentById(id);
      setAppointment(response.data);
    } catch (error) {
      if (error?.response?.data?.status === 404) {
        navigate('/404');
      } else {
        toast.error(messages.errors.error_data_loaning);
        console.log(error);
      }
    }
  };

  // Load property appointments history (lazy loading)
  const loadPropertyHistory = async () => {
    console.log("[loadPropertyHistory] Called, propertyAppointments:", propertyAppointments, "property.id:", appointment?.property?.id);
    if (propertyAppointments !== null || !appointment?.property?.id) return;
    
    setHistoryLoading(true);
    try {
      const response = await appointmentsServices.getByProperty(appointment.property.id, 50, 0);
      const history = (response.data || []).filter(apt => apt.id !== id);
      console.log("[loadPropertyHistory] Loaded history:", history.length, "items");
      setPropertyAppointments(history);
    } catch (error) {
      console.error("[loadPropertyHistory] Failed:", error);
      setPropertyAppointments([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleContactClientChange = async (clientId) => {
    try {
      await appointmentsServices.update(id, {
        contactClient: clientId ? { id: clientId } : null,
      });
      setAppointment((prev) => ({
        ...prev,
        contactClient: clientId
          ? prev.property?.clients?.find((cp) => cp.client?.id === clientId)?.client || { id: clientId }
          : null,
      }));
      toast.success('Primary contact updated');
    } catch (error) {
      console.error('Failed to update contact client:', error);
      toast.error('Failed to update primary contact');
    }
  };

  const handleBack = () => {
    if (isTechnician) {
      // Return to the date the technician was browsing on the Schedule page
      // (saved there in sessionStorage), not to today.
      const savedDate = sessionStorage.getItem('schedule_date');
      navigate(`/schedule${savedDate ? `?date=${savedDate}` : ''}`);
    } else {
      const savedDate = sessionStorage.getItem('appointments_date');
      const savedView = sessionStorage.getItem('appointments_view');
      const params = new URLSearchParams();
      if (savedDate) params.set('date', savedDate);
      if (savedView) params.set('view', savedView);
      const queryString = params.toString();
      navigate(`/appointments${queryString ? '?' + queryString : ''}`);
    }
  };

  // Hooks
  useEffect(() => {
    getAppointmentData();
    accordionToggle('details');
  }, []);

  useEffect(() => {
    if (appointment?.property?.id) {
      loadPropertyHistory();
    }
  }, [appointment?.property?.id]);

  // Check for data loading
  if (!appointment) return <Spinner />;

  const hasProperty = !!appointment.property;

  // Client/salesman pair for a deferred follow-up call created from this appointment
  const followUpClient = appointment.contactClient || appointment.property?.clients?.[0]?.client || null;

  const scheduledCallModal = (
    <ScheduledAppointmentModal
      open={schedModalOpen}
      onClose={() => setSchedModalOpen(false)}
      initialClient={followUpClient}
      initialSalesman={appointment.user || null}
      appointmentId={id}
    />
  );

  const addToScheduleButton = (
    <Button
      variant="outlined"
      size="small"
      startIcon={<PhoneCallbackIcon />}
      onClick={() => setSchedModalOpen(true)}
    >
      Add to schedule
    </Button>
  );

  // ===================== MOBILE VIEW =====================
  if (isMobile) {
    return (
      <Box sx={{ pb: 4 }}>
        <BrowserTitle title={`Appointment | Airmaxx Pro`} />

        {/* Back Button + Add to schedule */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Button
            variant="text"
            onClick={handleBack}
            startIcon={<KeyboardBackspaceIcon />}
            sx={{ ml: -1 }}
          >
            {isTechnician ? 'Back to Schedule' : 'Back'}
          </Button>
          {addToScheduleButton}
        </Box>

        {/* Appointment Details - always expanded on mobile */}
        <AppointmentDetailsTable
          appointment={appointment} isReadonly={isCancelled}
          setAppointment={setAppointment}
          id={id}
          refresh={getAppointmentData}
        />

        {!hasProperty && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This appointment has no linked property. Some sections may be unavailable.
          </Alert>
        )}

        {/* Appointments History */}
        {hasProperty && (
          <MobileSection 
            title="Appointments" 
            icon={CalendarTodayIcon}
            count={propertyAppointments?.length}
          >
            {historyLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : propertyAppointments && propertyAppointments.length > 0 ? (
              <AppointmentAppointmentsTable appointments={propertyAppointments} />
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                No previous appointments for this property
              </Typography>
            )}
          </MobileSection>
        )}

        {/* Archive - Legacy Appointments */}
        {appointment.property?.archiveAppointments && appointment.property.archiveAppointments.length > 0 && (
          <MobileSection 
            title="Archive (Legacy Appointments)" 
            icon={HistoryIcon}
            count={appointment.property.archiveAppointments.length}
          >
            <PropertyArchiveTable appointments={appointment.property.archiveAppointments} />
          </MobileSection>
        )}

        {/* Proposals */}
        <MobileSection 
          title="Proposals" 
          icon={DescriptionIcon}
          count={appointment?.proposals?.length}
        >
          <AppointmentProposalsTable proposals={appointment?.proposals} />
        </MobileSection>

        {/* Legacy Proposals */}
        {appointment.property?.legacyProposals && appointment.property.legacyProposals.length > 0 && (
          <MobileSection 
            title="Legacy Proposals" 
            icon={DescriptionIcon}
            count={appointment.property.legacyProposals.length}
          >
            <PropertyLegacyProposalsTable legacyProposals={appointment.property.legacyProposals} />
          </MobileSection>
        )}

        {/* Clients */}
        {hasProperty && (
          <MobileSection 
            title="Clients" 
            icon={PeopleIcon}
            count={appointment.property?.clients?.length}
          >
            {appointment.property?.clients?.length > 0 && (
              <Box sx={{ px: 2, pt: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="mobile-contactClient-label">Primary Contact</InputLabel>
                  <Select
                    labelId="mobile-contactClient-label"
                    value={appointment.contactClient?.id || ''}
                    label="Primary Contact"
                    onChange={(e) => handleContactClientChange(e.target.value || null)}
                    startAdornment={<ContactPhoneIcon sx={{ mr: 1, color: '#1976d2', fontSize: 20 }} />}
                  >
                    <MenuItem value="">
                      <em>Auto (first client)</em>
                    </MenuItem>
                    {appointment.property.clients.map((cp) => (
                      <MenuItem key={cp.client?.id} value={cp.client?.id}>
                        {cp.client?.fullName || `${cp.client?.firstName || ''} ${cp.client?.lastName || ''}`.trim()}
                        {cp.relation ? ` (${cp.relation})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Contact for Call/SMS</FormHelperText>
                </FormControl>
              </Box>
            )}
            <PropertyClientsTable 
              clientsList={appointment.property?.clients || []} 
              id={appointment.property?.id}
              refresh={getAppointmentData}
            />
          </MobileSection>
        )}

        {/* Warranty Tickets */}
        {hasProperty && (
          <MobileSection 
            title="Warranty Tickets" 
            icon={BuildIcon}
            count={warrantyTicketsCount}
          >
            <PropertyWarrantyTickets propertyId={appointment.property?.id} onCountChange={setWarrantyTicketsCount} />
          </MobileSection>
        )}

        {/* Media */}
        <MobileSection 
          title="Media" 
          icon={PhotoLibraryIcon}
          count={appointment?.media?.length}
          defaultExpanded={false}
        >
          <AppointmentMediaTable mediaList={appointment?.media} propertyMediaList={appointment?.property?.media} id={id} refresh={getAppointmentData} />
        </MobileSection>

        {scheduledCallModal}
      </Box>
    );
  }

  // ===================== DESKTOP VIEW =====================
  return (
    <Box component="section">
      <BrowserTitle title={`Appointment page | Airmaxx Pro`} />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Button
          variant="text"
          onClick={handleBack}
          startIcon={<KeyboardBackspaceIcon />}
        >
          {isTechnician ? 'Back to Schedule' : 'Back to Appointments'}
        </Button>
        {addToScheduleButton}
      </Box>

      {/* Cancelled Appointment Warning */}
      {isCancelled && (
        <Box sx={{ 
          mb: 2, 
          p: 2, 
          backgroundColor: '#ffebee', 
          border: '2px solid #f44336', 
          borderRadius: 2 
        }}>
          <Typography variant="h6" sx={{ color: '#c62828', fontWeight: 'bold', mb: 1 }}>
            ⚠️ CANCELLED APPOINTMENT - READ ONLY
          </Typography>
          {appointment?.reasons?.[0] && (
            <Typography variant="body1" sx={{ color: '#d32f2f' }}>
              <strong>Cancel Reason:</strong> {appointment.reasons[0].note}
              {appointment.reasons[0].createdBy && (
                <Typography variant="caption" component="span" sx={{ ml: 1, color: '#666' }}>
                  (by {appointment.reasons[0].createdBy.fullName})
                </Typography>
              )}
            </Typography>
          )}
        </Box>
      )}

      {!hasProperty && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This appointment has no linked property. Some sections may be unavailable.
        </Alert>
      )}

      {/* Appointment Details */}
      <Accordion
        defaultExpanded
        onChange={() => accordionToggle('details')}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body1">Appointment Details</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <AppointmentDetailsTable
            {...{
              appointment,
              setAppointment,
              id,
              refresh: getAppointmentData,
              isReadonly: isCancelled,
            }}
          />
        </AccordionDetails>
      </Accordion>

      {/* Appointments */}
      {hasProperty && (
        <Accordion
          defaultExpanded
          onChange={() => { accordionToggle('appointments'); loadPropertyHistory(); }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body1">Appointments</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {historyLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : propertyAppointments && propertyAppointments.length > 0 ? (
              <AppointmentAppointmentsTable appointments={propertyAppointments} />
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                No previous appointments for this property
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Archive - Legacy System Appointments */}
      {appointment.property?.archiveAppointments && appointment.property.archiveAppointments.length > 0 && (
        <Accordion onChange={() => accordionToggle('archive')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1">Archive (Legacy Appointments)</Typography>
              <Chip label={appointment.property.archiveAppointments.length} size="small" color="warning" />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <PropertyArchiveTable appointments={appointment.property.archiveAppointments} />
          </AccordionDetails>
        </Accordion>
      )}

      {/* Proposals */}
      <Accordion
        defaultExpanded
        onChange={() => accordionToggle('proposals')}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body1">Proposals</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <AppointmentProposalsTable proposals={appointment?.proposals} />
        </AccordionDetails>
      </Accordion>

      {/* Legacy Proposals */}
      {appointment.property?.legacyProposals && appointment.property.legacyProposals.length > 0 && (
        <Accordion onChange={() => accordionToggle('legacyProposals')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1">Legacy Proposals</Typography>
              <Chip label={appointment.property.legacyProposals.length} size="small" color="info" />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <PropertyLegacyProposalsTable legacyProposals={appointment.property.legacyProposals} />
          </AccordionDetails>
        </Accordion>
      )}

      {/* Clients */}
      {hasProperty && (
        <Accordion
          defaultExpanded
          onChange={() => accordionToggle('clients')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body1">Clients</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {appointment.property?.clients?.length > 0 && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="desktop-contactClient-label">Primary Contact</InputLabel>
                <Select
                  labelId="desktop-contactClient-label"
                  value={appointment.contactClient?.id || ''}
                  label="Primary Contact"
                  onChange={(e) => handleContactClientChange(e.target.value || null)}
                  startAdornment={<ContactPhoneIcon sx={{ mr: 1, color: '#1976d2' }} />}
                >
                  <MenuItem value="">
                    <em>Auto (first client)</em>
                  </MenuItem>
                  {appointment.property.clients.map((cp) => (
                    <MenuItem key={cp.client?.id} value={cp.client?.id}>
                      {cp.client?.fullName || `${cp.client?.firstName || ''} ${cp.client?.lastName || ''}`.trim()}
                      {cp.relation ? ` (${cp.relation})` : ''}
                      {cp.client?.phoneNumber ? ` — ${cp.client.phoneNumber}` : ''}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Select which client to contact for this appointment (Call/SMS)</FormHelperText>
              </FormControl>
            )}
            <PropertyClientsTable 
              clientsList={appointment.property?.clients || []} 
              id={appointment.property?.id}
              refresh={getAppointmentData}
            />
          </AccordionDetails>
        </Accordion>
      )}

      {/* Warranty Tickets */}
      {hasProperty && (
        <Accordion
          defaultExpanded={false}
          onChange={() => accordionToggle('warrantyTickets')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body1">Warranty Tickets ({warrantyTicketsCount})</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <PropertyWarrantyTickets propertyId={appointment.property?.id} onCountChange={setWarrantyTicketsCount} />
          </AccordionDetails>
        </Accordion>
      )}

      {/* Media */}
      <Accordion
        defaultExpanded
        onChange={() => accordionToggle('media')}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body1">Media</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <AppointmentMediaTable mediaList={appointment?.media} propertyMediaList={appointment?.property?.media} id={id} refresh={getAppointmentData} />
        </AccordionDetails>
      </Accordion>

      {scheduledCallModal}
    </Box>
  );
};

export default Appointment;
